import { useAuthStore } from "@nike/aegis-auth-react";
import { BACKEND_URL } from "../constants/app.constants";

const useClient = () => {
  const { isLoggedIn, accessToken, idClaims, client } = useAuthStore();

  const reloadIfNecessary = async () => {
    const clientAccessToken = client
      ? await client.getAccessToken()
      : undefined;
    if (
      !isLoggedIn ||
      !clientAccessToken ||
      clientAccessToken.expiresAt < Math.floor(Date.now() / 1000)
    ) {
      window.location.reload();
    }
  };
  const fetchWithAuth = async <T>(
    uri: string,
    setError: (text: string) => void,
  ): Promise<T> => {
    await reloadIfNecessary();
    const response = await fetch(`${BACKEND_URL}${uri}`, {
      method: "GET",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${accessToken}` ? `Bearer ${accessToken}` : "",
      },
    });
    if (response.ok) {
      return response.json();
    }
    const text = await response.text();

    setError(JSON.parse(text).details);
    return {} as T;
  };

  const reset = async <T>(
    uri: string,
    setError: (text: string) => void,
  ): Promise<T> => {
    await reloadIfNecessary();
    const response = await fetch(`${BACKEND_URL}${uri}`, {
      method: "DELETE",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${accessToken}` ? `Bearer ${accessToken}` : "",
      },
    });
    if (response.ok) {
      return response.json();
    }
    const text = await response.text();

    setError(JSON.parse(text).details);
    return {} as T;
  };
  const putData = async <T>(
    uri: string,
    data: T,
    setError: (text: string) => void,
  ): Promise<T> => {
    await reloadIfNecessary();
    const response = await fetch(`${BACKEND_URL}${uri}`, {
      method: "PUT",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${accessToken}` ? `Bearer ${accessToken}` : "",
      },
      body: JSON.stringify({ data }),
    });
    if (response.ok) {
      return response.json();
    }
    const text = await response.text();

    setError(JSON.parse(text).details);
    return {} as T;
  };
  const postData = async <T>(
    uri: string,
    data: T,
    setError: (text: string) => void,
  ): Promise<T | undefined> => {
    await reloadIfNecessary();
    const response = await fetch(`${BACKEND_URL}${uri}`, {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${accessToken}` ? `Bearer ${accessToken}` : "",
      },
      body: JSON.stringify({ data }),
    });
    if (response.ok) {
      return response.json();
    }
    const text = await response.text();

    setError(JSON.parse(text).details);
  };
  const id = idClaims ?? { name: "", email: "" };
  const name = id.name?.length ? id.name : id.email ?? "";

  // Groups is definitely on there
  const groups = idClaims?.groups ?? ["Unknown"];
  const splitGroup = groups[0].split(".");
  const group: string = splitGroup[splitGroup.length - 1];
  const role =
    group === "seai"
      ? "Sea&i"
      : `${group.substring(0, 1).toUpperCase()}${group.substring(1)}`;

  return { postData, putData, reset, fetchWithAuth, role, name };
};

export default useClient;
